import React, { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
    Container, Box, ToggleButton, ToggleButtonGroup, Typography, Button,
    Snackbar, Alert 
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

// Import các Tab con
import PerformanceTab from './management/PerformanceTab';
import BookingListTab from './management/BookingListTab';
import PayableListTab from './management/PayableListTab';
import AccountingTab from './management/AccountingTab';
import SystemControl from './management/SystemControl';

const managementTheme = createTheme({
    typography: {
        fontFamily: "'Roboto', 'Arial', sans-serif",
        button: { textTransform: 'none', fontWeight: 'bold' },
        allVariants: { fontFeatureSettings: "'cv11', 'ss01'" }
    },
    shape: { borderRadius: 8 },
    palette: { primary: { main: '#007373' } },
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: { backgroundColor: '#fff' }
            }
        }
    }
});

function ManagementDashboard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const viewMode = searchParams.get('tab') || 'OVERVIEW';
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const [canExport, setCanExport] = useState(true);

    const tabRef = useRef();

    React.useEffect(() => {
        setCanExport(true);
    }, [viewMode]);

    const handleRefresh = () => {
        if (tabRef.current && tabRef.current.resetFilters) {
            tabRef.current.resetFilters();
        } else if (tabRef.current && tabRef.current.loadData) {
            tabRef.current.loadData();
        }
    };

    const handleChangeTab = (e, newMode) => {
        if (newMode) {
            const currentParams = Object.fromEntries([...searchParams]);
            setSearchParams({ ...currentParams, tab: newMode });
        }
    };

    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };

    const handleExportReport = () => {
        if (viewMode === 'OVERVIEW') return; // Tab tổng quan chưa hỗ trợ hoặc không cần
        
        if (tabRef.current && tabRef.current.openExportDialog) {
            tabRef.current.openExportDialog();
        }
    };

    return (
        <ThemeProvider theme={managementTheme}>
            <Container maxWidth={false} sx={{ mt: 3, mb: 3, maxWidth: '1400px' }}>
                
                {/* --- HEADER --- */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#007373' }}>
                        QUẢN TRỊ HỆ THỐNG
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={handleChangeTab}
                            size="small"
                            sx={{ bgcolor: 'white' }}
                        >
                            <ToggleButton value="OVERVIEW" sx={{ px: 3 }}>Tổng quan</ToggleButton>
                            <ToggleButton value="BOOKING" sx={{ px: 3 }}>QL Booking</ToggleButton>
                            <ToggleButton value="PAYABLE" sx={{ px: 3 }}>QL Công nợ</ToggleButton>
                            <ToggleButton value="ACCOUNTING" sx={{ px: 3 }}>Kế toán</ToggleButton>
                        </ToggleButtonGroup>

                        {/* --- COMPONENT KIỂM SOÁT HỆ THỐNG --- */}
                        <SystemControl 
                            onRefreshData={handleRefresh} 
                            showToast={showToast} 
                        />

                        <Button 
                            variant="outlined" 
                            startIcon={<FileDownloadIcon />} 
                            onClick={handleExportReport}
                            disabled={viewMode === 'OVERVIEW' || !canExport} 
                            sx={{ 
                                bgcolor: 'white', 
                                color: '#007373', 
                                borderColor: '#007373',
                                '&:hover': {
                                    bgcolor: '#e0f2f1',
                                    borderColor: '#004d40'
                                },
                                '&.Mui-disabled': {
                                    color: '#999',
                                    borderColor: '#ccc',
                                    bgcolor: '#f5f5f5'
                                }
                            }}
                        >
                            Xuất Báo cáo
                        </Button>

                        <Button 
                            variant="outlined" 
                            startIcon={<RefreshIcon />} 
                            onClick={handleRefresh}
                            sx={{ bgcolor: 'white' }}
                        >
                            Làm mới
                        </Button>
                    </Box>
                </Box>

                {/* --- CONTENT --- */}
                <Box>
                    {viewMode === 'OVERVIEW' && <PerformanceTab ref={tabRef} />}
                    {viewMode === 'BOOKING' && <BookingListTab ref={tabRef} />}
                    {viewMode === 'PAYABLE' && <PayableListTab ref={tabRef} />}
                    {viewMode === 'ACCOUNTING' && <AccountingTab ref={tabRef} setCanExport={setCanExport} />}
                </Box>

                <Snackbar 
                    open={toast.open} 
                    autoHideDuration={4000} 
                    onClose={() => setToast({...toast, open: false})}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert severity={toast.severity} variant="filled">{toast.message}</Alert>
                </Snackbar>
            </Container>
        </ThemeProvider>
    );
}

export default ManagementDashboard;